import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useShop } from "../store/ShopContext";
import { api, formatPrice } from "../lib/api";
import { Wordmark } from "../components/Wordmark";

const STEPS = ["Date de contact", "Livrare", "Plată", "Confirmare"];

const Field = ({ label, name, value, onChange, type = "text", required = true, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="eyebrow block mb-2">{label}</span>
    <input data-testid={`checkout-${name}`} name={name} type={type} required={required} value={value} onChange={(e) => onChange(name, e.target.value)} className="input-cc" />
  </label>
);

const Option = ({ selected, onClick, title, desc, price, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick} className={`w-full text-left p-5 border transition-colors duration-200 flex items-start gap-4 ${selected ? "border-[#0A0A0A] bg-white" : "border-black/15 hover:border-black/40"}`}>
    <span className={`mt-0.5 h-4 w-4 border flex items-center justify-center ${selected ? "bg-[#0A0A0A] border-[#0A0A0A]" : "border-black/30"}`}>{selected && <Check size={10} className="text-white" />}</span>
    <span className="flex-1">
      <span className="flex justify-between font-medium text-sm"><span>{title}</span><span>{price}</span></span>
      <span className="block text-xs text-[#737373] mt-1">{desc}</span>
    </span>
  </button>
);

export default function Checkout() {
  const { cart, cartTotal, clearCart } = useShop();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", address: "", city: "", county: "", postal_code: "" });
  const [shipping, setShipping] = useState("courier");
  const [payment, setPayment] = useState("card");
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const shipCost = shipping === "express" ? 49 : cartTotal >= 500 ? 0 : 29;

  if (cart.length === 0) {
    return (
      <div data-testid="checkout-empty" className="container-cc py-32 text-center">
        <p className="font-serif text-4xl">Nu ai produse în coș</p>
        <Link to="/catalog" className="btn-outline mt-8">Înapoi la catalog</Link>
      </div>
    );
  }

  const next = (e) => {
    e?.preventDefault();
    setStep((s) => Math.min(s + 1, 3));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        items: cart.map((i) => ({ slug: i.slug, name: i.name, price: i.price, qty: i.qty, variant: i.variant, image: i.image })),
        customer: form,
        shipping_method: shipping,
        payment_method: payment,
      });
      clearCart();
      navigate(`/comanda/${data.order_number}`);
    } catch {
      toast.error("Nu am putut plasa comanda demo. Verifică datele.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="min-h-screen bg-white">
      <div className="border-b border-black/10">
        <div className="container-cc h-[72px] flex items-center justify-between">
          <Wordmark />
          <Link to="/cos" className="text-[12px] uppercase tracking-[0.14em] font-semibold inline-flex items-center gap-2 link-underline"><ArrowLeft size={14} /> Înapoi la coș</Link>
        </div>
      </div>
      <div className="container-cc py-10 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-7">
          <ol data-testid="checkout-steps" className="flex items-center gap-3 mb-10 overflow-x-auto no-scrollbar">
            {STEPS.map((s, i) => (
              <li key={s} className="flex items-center gap-3 shrink-0">
                <span className={`h-7 w-7 flex items-center justify-center text-[11px] font-semibold border ${i <= step ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/20 text-[#737373]"}`}>{i < step ? <Check size={12} /> : i + 1}</span>
                <span className={`text-[11px] uppercase tracking-[0.16em] font-semibold ${i <= step ? "text-[#0A0A0A]" : "text-[#9A9A9A]"}`}>{s}</span>
                {i < STEPS.length - 1 && <span className="w-8 h-px bg-black/15" />}
              </li>
            ))}
          </ol>

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
              {step === 0 && (
                <form onSubmit={next} className="space-y-6">
                  <h2 className="font-serif text-3xl sm:text-4xl font-light">Date de contact</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Prenume" name="first_name" value={form.first_name} onChange={set} />
                    <Field label="Nume" name="last_name" value={form.last_name} onChange={set} />
                    <Field label="Email" name="email" type="email" value={form.email} onChange={set} />
                    <Field label="Telefon" name="phone" type="tel" value={form.phone} onChange={set} />
                  </div>
                  <button data-testid="checkout-next-1" className="btn-primary">Continuă spre livrare <ArrowRight size={16} /></button>
                </form>
              )}
              {step === 1 && (
                <form onSubmit={next} className="space-y-6">
                  <h2 className="font-serif text-3xl sm:text-4xl font-light">Livrare</h2>
                  <div className="space-y-3">
                    <Option testId="shipping-courier" selected={shipping === "courier"} onClick={() => setShipping("courier")} title="Curier la domiciliu" desc="Termenele și costurile reale vor fi stabilite la lansare." price={cartTotal >= 500 ? "Gratuit" : "29 lei"} />
                    <Option testId="shipping-express" selected={shipping === "express"} onClick={() => setShipping("express")} title="Curier express (demo)" desc="Livrare prioritară, cu programare telefonică înainte de sosire." price="49 lei" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Adresă" name="address" value={form.address} onChange={set} className="sm:col-span-2" />
                    <Field label="Oraș" name="city" value={form.city} onChange={set} />
                    <Field label="Județ" name="county" value={form.county} onChange={set} />
                    <Field label="Cod poștal" name="postal_code" value={form.postal_code} onChange={set} required={false} />
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setStep(0)} className="btn-outline">Înapoi</button>
                    <button data-testid="checkout-next-2" className="btn-primary">Continuă spre plată <ArrowRight size={16} /></button>
                  </div>
                </form>
              )}
              {step === 2 && (
                <form onSubmit={next} className="space-y-6">
                  <h2 className="font-serif text-3xl sm:text-4xl font-light">Plată</h2>
                  <div className="space-y-3">
                    <Option testId="payment-card" selected={payment === "card"} onClick={() => setPayment("card")} title="Card bancar (demo)" desc="Integrarea cu procesatorul de plăți va fi activată în faza următoare. Nu se procesează nicio plată." price="" />
                    <Option testId="payment-cod" selected={payment === "cod"} onClick={() => setPayment("cod")} title="Ramburs la livrare (demo)" desc="Disponibilitatea rambursului va fi confirmată la lansare." price="" />
                    <Option testId="payment-transfer" selected={payment === "transfer"} onClick={() => setPayment("transfer")} title="Transfer bancar (demo)" desc="Detaliile contului vor fi comunicate pe email." price="" />
                  </div>
                  {payment === "card" && (
                    <div className="surface p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 opacity-70">
                      <label className="sm:col-span-3 block"><span className="eyebrow block mb-2">Număr card</span><input disabled placeholder="•••• •••• •••• ••••" className="input-cc" /></label>
                      <label className="block"><span className="eyebrow block mb-2">Expiră</span><input disabled placeholder="LL/AA" className="input-cc" /></label>
                      <label className="block"><span className="eyebrow block mb-2">CVC</span><input disabled placeholder="•••" className="input-cc" /></label>
                      <p className="sm:col-span-3 text-xs text-[#737373] flex items-center gap-2"><Lock size={12} /> Câmpuri dezactivate în demo. Nicio plată reală nu este procesată.</p>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setStep(1)} className="btn-outline">Înapoi</button>
                    <button data-testid="checkout-next-3" className="btn-primary">Revizuiește comanda <ArrowRight size={16} /></button>
                  </div>
                </form>
              )}
              {step === 3 && (
                <div className="space-y-8">
                  <h2 className="font-serif text-3xl sm:text-4xl font-light">Confirmare</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                    <div><p className="eyebrow mb-2">Contact</p><p>{form.first_name} {form.last_name}</p><p className="text-[#737373]">{form.email} · {form.phone}</p></div>
                    <div><p className="eyebrow mb-2">Livrare</p><p>{shipping === "express" ? "Curier express" : "Curier la domiciliu"}</p><p className="text-[#737373]">{form.address}, {form.city}, {form.county}</p></div>
                    <div><p className="eyebrow mb-2">Plată</p><p>{{ card: "Card bancar (demo)", cod: "Ramburs (demo)", transfer: "Transfer bancar (demo)" }[payment]}</p></div>
                  </div>
                  <div className="surface p-5 text-xs text-[#5A5A5A] leading-relaxed">
                    Prin plasarea acestei comenzi demo nu se încheie niciun contract și nu se procesează nicio plată. Scopul este exclusiv prezentarea fluxului de cumpărare.
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setStep(2)} className="btn-outline">Înapoi</button>
                    <button data-testid="checkout-place-order" disabled={submitting} onClick={submit} className="btn-primary">{submitting ? "Se procesează…" : "Plasează comanda demo"}</button>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <aside className="lg:col-span-5">
          <div className="surface p-8 lg:sticky lg:top-10" data-testid="checkout-summary">
            <p className="font-serif text-2xl mb-6">Comanda ta</p>
            <ul className="divide-y divide-black/5">
              {cart.map((i) => (
                <li key={i.key} className="py-4 flex items-center gap-4">
                  <div className="bg-white w-16 h-16 flex items-center justify-center p-1.5 shrink-0 relative">
                    <img src={i.image} alt={i.name} className="product-img max-h-full object-contain" />
                    <span className="absolute -top-2 -right-2 h-5 min-w-[20px] px-1 bg-[#0A0A0A] text-white text-[10px] flex items-center justify-center">{i.qty}</span>
                  </div>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{i.name}</p><p className="text-xs text-[#737373]">{i.variant || i.color}</p></div>
                  <p className="text-sm font-semibold">{formatPrice(i.price * i.qty)}</p>
                </li>
              ))}
            </ul>
            <dl className="mt-6 pt-6 border-t border-black/10 space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-[#737373]">Subtotal</dt><dd>{formatPrice(cartTotal)}</dd></div>
              <div className="flex justify-between"><dt className="text-[#737373]">Livrare</dt><dd>{shipCost === 0 ? "Gratuit" : formatPrice(shipCost)}</dd></div>
              <div className="flex justify-between text-base pt-3 border-t border-black/10"><dt className="font-semibold">Total</dt><dd data-testid="checkout-total" className="font-semibold">{formatPrice(cartTotal + shipCost)}</dd></div>
            </dl>
          </div>
        </aside>
      </div>
    </div>
  );
}
